import apiClient, { PaginatedResponse } from './client';

export type ServiceApprovalStatus = 'draft' | 'pending' | 'approved' | 'rejected';

export interface ServiceGalleryImage {
    id: number;
    image: string;
    caption?: string;
    sort_order?: number;
}

export interface AutomotiveService {
    id: number;
    provider_store?: number | null;
    provider_store_name?: string;
    provider_vendor_type?: string;
    service_type: string;
    name: string;
    name_ar?: string;
    logo?: string;
    short_description?: string;
    short_description_ar?: string;
    full_description?: string;
    full_description_ar?: string;
    video_url?: string;
    price_label?: string;
    service_duration?: string;
    service_area?: string;
    service_area_ar?: string;
    highlights?: string[];
    rating_average?: number | string | null;
    rating_count?: number;
    contact_phone?: string;
    website_url?: string;
    latitude?: number;
    longitude?: number;
    address?: string;
    address_ar?: string;
    is_featured: boolean;
    is_active: boolean;
    approval_status: ServiceApprovalStatus;
    rejection_reason?: string;
    sort_order: number;
    gallery_images?: ServiceGalleryImage[];
    distance_km?: number;
}

export interface ServiceAssistanceRequest {
    id: number;
    service: number;
    service_name?: string;
    service_type?: string;
    service_provider_name?: string;
    customer_name?: string;
    customer_email?: string;
    customer_phone?: string;
    user_latitude?: number;
    user_longitude?: number;
    status: 'pending' | 'accepted' | 'en_route' | 'completed' | 'cancelled';
    notes?: string;
    created_at?: string;
    updated_at?: string;
}

export interface ServicesFilter {
    service_type?: string;
    is_featured?: boolean;
    search?: string;
    lat?: number;
    lng?: number;
    ordering?: string;
    page?: number;
    page_size?: number;
}

export const servicesApi = {
    // Services Getters
    getServices: async (filters?: ServicesFilter) => {
        const { data } = await apiClient.get<PaginatedResponse<AutomotiveService>>('/services/services/', {
            params: filters,
            skipAuth: true,
        } as any);
        return data;
    },

    getService: async (id: number) => {
        const { data } = await apiClient.get<AutomotiveService>(`/services/services/${id}/`, {
            skipAuth: true,
        } as any);
        return data;
    },

    getMyServices: async () => {
        const { data } = await apiClient.get<PaginatedResponse<AutomotiveService>>('/services/services/', { params: { scope: 'mine' } });
        return data;
    },

    getAllServicesForAdmin: async () => {
        const { data } = await apiClient.get<PaginatedResponse<AutomotiveService>>('/services/services/', { params: { scope: 'all' } });
        return data;
    },

    createService: async (payload: Partial<AutomotiveService> | FormData) => {
        const isFormData = payload instanceof FormData;
        const { data } = await apiClient.post<AutomotiveService>(
            '/services/services/',
            payload,
            isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined
        );
        return data;
    },

    updateService: async (id: number, payload: Partial<AutomotiveService> | FormData) => {
        const isFormData = payload instanceof FormData;
        const { data } = await apiClient.patch<AutomotiveService>(
            `/services/services/${id}/`,
            payload,
            isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined
        );
        return data;
    },

    approveService: async (id: number) => {
        const { data } = await apiClient.post<AutomotiveService>(`/services/services/${id}/approve/`);
        return data;
    },

    rejectService: async (id: number, reason?: string) => {
        const { data } = await apiClient.post<AutomotiveService>(`/services/services/${id}/reject/`, { reason: reason || '' });
        return data;
    },

    submitService: async (id: number) => {
        const { data } = await apiClient.post<AutomotiveService>(`/services/services/${id}/submit/`);
        return data;
    },

    // Assistance Requests
    getRequests: async () => {
        const { data } = await apiClient.get<PaginatedResponse<ServiceAssistanceRequest>>('/services/requests/');
        return data;
    },

    createRequest: async (requestData: Partial<ServiceAssistanceRequest>) => {
        const { data } = await apiClient.post<ServiceAssistanceRequest>('/services/requests/', requestData);
        return data;
    },

    updateRequestStatus: async (id: number, status: ServiceAssistanceRequest['status']) => {
        const { data } = await apiClient.patch<ServiceAssistanceRequest>(`/services/requests/${id}/`, { status });
        return data;
    },

    cancelRequest: async (id: number) => {
        const { data } = await apiClient.patch<ServiceAssistanceRequest>(`/services/requests/${id}/`, { status: 'cancelled' });
        return data;
    }
};
